import type { Message } from '../types';

// IndexedDB-backed message history, so it survives a reload and stays usable
// offline instead of living only in memory (and, before this, a 30-message
// slice in localStorage - synchronous, ~5 MB, blocking on every write).
//
// One object store, keyed by [channel, id], with an index on [channel, date]
// for "newest N" and "N older than X" reads. Writes are coalesced into one
// transaction per flush; each channel is pruned to MAX_ROWS_PER_CHANNEL. Every
// entry point degrades to a no-op when IndexedDB is unavailable (private
// mode, jsdom, blocked storage) - the in-memory store keeps working.
//
// The server stays the source of truth: this is a cache, safe to lose.

const DB_NAME = 'zmist-signals';
const DB_VERSION = 1;
const STORE = 'signal';
const BY_DATE = 'byChannelDate';
export const MAX_ROWS_PER_CHANNEL = 20_000;
const FLUSH_DELAY_MS = 250;
const PRUNE_EVERY_ROWS = 500;

let dbPromise: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const store = req.result.createObjectStore(STORE, { keyPath: ['channel', 'id'] });
        store.createIndex(BY_DATE, ['channel', 'date']);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

interface Row extends Omit<Message, 'syncStatus'> {
  channel: string;
}

function toRow(channel: string, m: Message): Row {
  // Local-only send state is never persisted.
  const { syncStatus: _s, ...rest } = m as Message & { syncStatus?: unknown };
  return { ...(rest as Omit<Message, 'syncStatus'>), channel };
}

function fromRow(row: Row): Message {
  const { channel: _c, ...m } = row;
  return m as Message;
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}

// ---- writes (coalesced) ----

const pending = new Map<string, Map<string, Row>>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const rowsSincePrune = new Map<string, number>();

/** Queues these messages for a single batched write. Unsynced (optimistic) ones are skipped. */
export function saveMessages(channel: string, messages: readonly Message[]): void {
  if (typeof indexedDB === 'undefined') return;
  let rows = pending.get(channel);
  for (const m of messages) {
    if (m.syncStatus) continue;
    if (!rows) pending.set(channel, (rows = new Map()));
    rows.set(m.id, toRow(channel, m));
  }
  if (!flushTimer && pending.size > 0) flushTimer = setTimeout(() => void flush(), FLUSH_DELAY_MS);
}

export async function flush(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pending.size === 0) return;
  const batch = [...pending.entries()];
  pending.clear();
  const db = await open();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    for (const [, rows] of batch) for (const row of rows.values()) store.put(row);
    await done(tx);
    for (const [channel, rows] of batch) {
      const total = (rowsSincePrune.get(channel) ?? 0) + rows.size;
      if (total >= PRUNE_EVERY_ROWS) {
        rowsSincePrune.set(channel, 0);
        await prune(db, channel);
      } else rowsSincePrune.set(channel, total);
    }
  } catch {
    /* cache only - ignore */
  }
}

async function prune(db: IDBDatabase, channel: string): Promise<void> {
  const range = IDBKeyRange.bound([channel, ''], [channel, '￿']);
  const count = await new Promise<number>((resolve) => {
    const req = db.transaction(STORE).objectStore(STORE).index(BY_DATE).count(range);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(0);
  });
  let excess = count - MAX_ROWS_PER_CHANNEL;
  if (excess <= 0) return;
  const tx = db.transaction(STORE, 'readwrite');
  // Oldest first.
  const cursorReq = tx.objectStore(STORE).index(BY_DATE).openCursor(range, 'next');
  cursorReq.onsuccess = () => {
    const cursor = cursorReq.result;
    if (!cursor || excess <= 0) return;
    cursor.delete();
    excess--;
    cursor.continue();
  };
  await done(tx);
}

// ---- reads ----

function readPage(channel: string, upper: string | null, limit: number): Promise<Message[]> {
  return open().then(
    (db) =>
      new Promise<Message[]>((resolve) => {
        if (!db) return resolve([]);
        try {
          // `upper` is exclusive when given ("strictly older than").
          const range = IDBKeyRange.bound([channel, ''], [channel, upper ?? '￿'], false, upper !== null);
          const out: Message[] = [];
          const req = db.transaction(STORE).objectStore(STORE).index(BY_DATE).openCursor(range, 'prev');
          req.onsuccess = () => {
            const cursor = req.result;
            if (!cursor || out.length >= limit) return resolve(out);
            out.push(fromRow(cursor.value as Row));
            cursor.continue();
          };
          req.onerror = () => resolve(out);
        } catch {
          resolve([]);
        }
      })
  );
}

/** Newest `limit` cached messages, newest-first. */
export function loadNewest(channel: string, limit: number): Promise<Message[]> {
  return readPage(channel, null, limit);
}

/** Up to `limit` cached messages strictly older than `beforeDate` (ISO), newest-first. */
export function loadOlder(channel: string, beforeDate: string, limit: number): Promise<Message[]> {
  return readPage(channel, beforeDate, limit);
}

// ---- deletes ----

export async function clearChannel(channel: string): Promise<void> {
  pending.delete(channel);
  const db = await open();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(IDBKeyRange.bound([channel, ''], [channel, '￿']));
    await done(tx);
  } catch {
    /* ignore */
  }
}

/** Drops everything - used on logout so a signed-out browser keeps no history. */
export async function clearAll(): Promise<void> {
  pending.clear();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const db = await open();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).clear();
    await done(tx);
  } catch {
    /* ignore */
  }
}

/** Cached message rows per channel - for the Settings > Database panel. */
export async function countByChannel(): Promise<Record<string, number>> {
  const db = await open();
  const out: Record<string, number> = {};
  if (!db) return out;
  try {
    const store = db.transaction(STORE, 'readonly').objectStore(STORE);
    await new Promise<void>((resolve) => {
      const req = store.openKeyCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return resolve();
        const channel = (cur.key as [string, string])[0];
        out[channel] = (out[channel] ?? 0) + 1;
        cur.continue();
      };
      req.onerror = () => resolve();
    });
  } catch {
    /* ignore */
  }
  return out;
}
